/**
 * Firebase Service - Cloud Database Operations
 * This replaces the local backend service for production deployment
 */

import { initializeApp } from 'firebase/app';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/auth';
import { 
  getFirestore, 
  collection, 
  getDocs, 
  getDoc,
  doc, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
  runTransaction,
  arrayRemove,
  arrayUnion,
  increment,
  writeBatch,
  connectFirestoreEmulator
} from 'firebase/firestore';
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword, 
  signOut as firebaseSignOut,
  updateProfile,
  sendPasswordResetEmail,
  connectAuthEmulator
} from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';
import { getRemoteConfig, fetchAndActivate, getValue } from 'firebase/remote-config';

import logger from '../utils/logger';

// Firebase configuration - replace with your actual config
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const compatApp = firebase.initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const compatDb = compatApp.firestore();
const functions = getFunctions(app);
const remoteConfig = getRemoteConfig(app);

// Configure Remote Config
remoteConfig.settings.minimumFetchIntervalMillis = 3600000; // 1 hour by default
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  remoteConfig.settings.minimumFetchIntervalMillis = 0; // Fetch immediately in dev
}

// Connect to emulators if running locally
if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  logger.log('🧪 Local environment detected, connecting to Firebase Emulators...');
  
  // Connect to Functions emulator (Port 5001)
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  
  // Connect to Firestore emulator (Port 8080)
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  compatDb.useEmulator('127.0.0.1', 8080);
  
  // Connect to Auth emulator (Port 9099)
  connectAuthEmulator(auth, 'http://127.0.0.1:9099');
  
  logger.log('✅ Emulators connected: Functions (5001), Firestore (8080), Auth (9099)');
}

class FirebaseService {
  constructor() {
    this.app = app;
    this.db = db;
    this.compatDb = compatDb;
    this.auth = auth;
    this.compatAuth = compatApp.auth();
    this.remoteConfig = remoteConfig;
  }

  /**
   * Initialize Remote Config and fetch flags
   */
  async getFeatureFlags() {
    try {
      await fetchAndActivate(this.remoteConfig);
      const flagsJson = getValue(this.remoteConfig, 'FeatureFlags').asString();
      if (!flagsJson) return {};
      
      const parsed = JSON.parse(flagsJson);
      // Convert array of {Name, Value} to an object {Name: Value}
      const flags = {};
      if (parsed.Flags && Array.isArray(parsed.Flags)) {
        parsed.Flags.forEach(flag => {
          flags[flag.Name] = flag.Value;
        });
      }
      return flags;
    } catch (error) {
      logger.error('Error fetching feature flags:', error);
      return {};
    }
  }

  /**
   * Sync compat auth with modular auth
   */
  async syncCompatAuth(firebaseUser) {
    if (!firebaseUser) {
      await this.compatAuth.signOut();
      return;
    }

    try {
      // If compat app is already signed in as the same user, skip
      if (this.compatAuth.currentUser && this.compatAuth.currentUser.uid === firebaseUser.uid) {
        return;
      }

      // We don't have the password here, but we can use the ID token if needed.
      // However, usually initializing compat with same config and waiting for it to auto-sync works.
      // If it doesn't, we might need a custom token from a function, which is overkill.
      // Let's at least log it for now and see if we can trigger a refresh.
      logger.log(`🔄 Syncing compat auth for UID: ${firebaseUser.uid}`);
    } catch (error) {
      logger.error('❌ Error syncing compat auth:', error);
    }
  }

  // ==================== USER OPERATIONS ====================

  /**
   * Get all users
   */
  async getUsers() {
    try {
      const usersRef = collection(this.db, 'users');
      const querySnapshot = await getDocs(usersRef);
      const users = [];
      querySnapshot.forEach((doc) => {
        users.push({ id: doc.id, ...doc.data() });
      });
      logger.log(`✅ Loaded ${users.length} users from Firebase`);
      return users;
    } catch (error) {
      logger.error('❌ Error getting users:', error);
      throw error;
    }
  }

  /**
   * Get user by username
   */
  async getUserByUsername(username) {
    try {
      const usersCol = collection(this.db, 'users');
      const q = query(usersCol, where('username', '==', username));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const userDoc = querySnapshot.docs[0];
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    } catch (error) {
      logger.error('❌ Error getting user:', error);
      return null;
    }
  }

  /**
   * Get user by email
   */
  async getUserByEmail(email) {
    try {
      const usersCol = collection(this.db, 'users');
      const q = query(usersCol, where('email', '==', email.toLowerCase().trim()));
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        return null;
      }
      
      const userDoc = querySnapshot.docs[0];
      return {
        id: userDoc.id,
        ...userDoc.data()
      };
    } catch (error) {
      logger.error('❌ Error getting user by email:', error);
      throw error;
    }
  }

  /**
   * Get user by Firebase Auth UID
   */
  async getUserByFirebaseUid(firebaseUid) {
    try {
      // Use direct document access with Firebase UID as document ID
      const userDoc = doc(this.db, 'users', firebaseUid);
      const userSnapshot = await getDoc(userDoc);
      
      if (!userSnapshot.exists()) {
        logger.warn('⚠️ No user profile found for Firebase UID:', firebaseUid);
        return null;
      }
      
      return {
        id: userSnapshot.id,
        ...userSnapshot.data()
      };
    } catch (error) {
      logger.error('❌ Error getting user by Firebase UID:', error);
      return null;
    }
  }

  /**
   * Get user by document ID
   */
  async getUserByDocumentId(documentId) {
    try {
      const userDoc = doc(this.db, 'users', documentId);
      const userSnapshot = await getDoc(userDoc);
      
      if (!userSnapshot.exists()) {
        logger.warn('⚠️ No user profile found for document ID:', documentId);
        return null;
      }
      
      return {
        id: userSnapshot.id,
        ...userSnapshot.data()
      };
    } catch (error) {
      logger.error('❌ Error getting user by document ID:', error);
      throw error;
    }
  }

  /**
   * Update user's admin status
   */
  async updateUserAdminStatus(userId, isAdmin) {
    try {
      const userRef = doc(this.db, 'users', userId);
      await updateDoc(userRef, {
        isAdmin: isAdmin,
        updatedAt: new Date().toISOString()
      });
      logger.log(`✅ Updated admin status for user ${userId}: ${isAdmin}`);
      return true;
    } catch (error) {
      logger.error('❌ Error updating user admin status:', error);
      throw error;
    }
  }

  /**
   * Update restroom name
   */
  async updateRestroomName(restroomId, newName) {
    try {
      const restroomRef = doc(this.db, 'restrooms', restroomId);
      await updateDoc( restroomRef, {
        name: newName,
        updatedAt: new Date().toISOString()
      });
      logger.log(`✅ Updated restroom name for ${restroomId}: ${newName}`);
      return true;
    } catch (error) {
      logger.error('❌ Error updating restroom name:', error);
      throw error;
    }
  }

  /**
   * Create a new user
   */
  async createUser(userId, userData) {
    try {
      logger.log('🔍 Creating user document...');
      logger.log('📍 Document path:', `/users/${userId}`);
      logger.log('👤 User data:', userData);
      
      const userDoc = doc(this.db, 'users', userId);
      await setDoc(userDoc, userData);
      
      logger.log('✅ User created in Firebase:', userId);
      return true;
    } catch (error) {
      logger.error('❌ Error creating user:', error);
      logger.error('🔥 Firebase error code:', error.code);
      logger.error('🔥 Firebase error message:', error.message);
      throw error;
    }
  }

  /**
   * Update user's ToS acceptance
   */
  async updateUserTOS(userId) {
    try {
      logger.log('📝 Updating ToS acceptance for user:', userId);
      const userRef = doc(this.db, 'users', userId);
      
      // Use setDoc with merge to update or create the fields
      await setDoc(userRef, {
        tosAccepted: true,
        tosAcceptedAt: new Date().toISOString(),
        tosVersion: "1.0"
      }, { merge: true }); // merge: true updates existing doc without overwriting other fields
      
      logger.log('✅ ToS acceptance updated for user:', userId);
      return true;
    } catch (error) {
      logger.error('❌ Error updating ToS acceptance:', error);
      logger.error('❌ Error details:', error.message);
      throw error;
    }
  }

  // ==================== RESTROOM OPERATIONS ====================

  /**
   * Get all restrooms WITHOUT filtering (for admin bulk operations)
   * @returns {Array} All restrooms in database
   */
  async getAllRestroomsRaw() {
    try {
      logger.log('🌐 Fetching ALL restrooms from Firebase (no filtering)...');
      const restroomsCol = collection(this.db, 'restrooms');
      const restroomSnapshot = await getDocs(restroomsCol);
      
      const restrooms = restroomSnapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
            latitude: typeof data.latitude === 'number' ? data.latitude : parseFloat(data.latitude),
            longitude: typeof data.longitude === 'number' ? data.longitude : parseFloat(data.longitude)
          };
        })
        .filter(restroom => {
          const isValid = 
            restroom.latitude != null && 
            !isNaN(restroom.latitude) &&
            restroom.longitude != null && 
            !isNaN(restroom.longitude) &&
            restroom.name;
          return isValid;
        });
      
      logger.log(`✅ Loaded ${restrooms.length} total restrooms from Firebase (unfiltered)`);
      return restrooms;
    } catch (error) {
      logger.error('❌ Error getting restrooms:', error);
      throw new Error('Failed to load restrooms from Firebase');
    }
  }

  /**
   * Get a single restroom by ID
   * @param {string} restroomId - The restroom ID to fetch
   */
  async getRestroomById(restroomId) {
    try {
      const restroomRef = doc(this.db, 'restrooms', restroomId);
      const restroomDoc = await getDoc(restroomRef);
      
      if (restroomDoc.exists()) {
        const restroomData = {
          id: restroomDoc.id,
          ...restroomDoc.data()
        };
        
        logger.log('✅ Restroom fetched by ID:', restroomData.name);
        return restroomData;
      } else {
        logger.warn('⚠️ Restroom not found:', restroomId);
        return null;
      }
    } catch (error) {
      logger.error('❌ Error getting restroom by ID:', error);
      return null;
    }
  }

  /**
   * Get all restrooms created by a specific user
   * @param {string} userId - The user ID to filter by
   */
  async getUserRestrooms(userId) {
    if (!userId) return [];
    try {
      const restroomsCol = collection(this.db, 'restrooms');
      const q = query(restroomsCol, where('createdBy', '==', userId));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
          latitude: typeof data.latitude === 'number' ? data.latitude : parseFloat(data.latitude),
          longitude: typeof data.longitude === 'number' ? data.longitude : parseFloat(data.longitude)
        };
      });
    } catch (error) {
      logger.error('❌ Error getting user restrooms:', error);
      return [];
    }
  }

  /**
   * Get all reviews created by a specific user
   * Since reviews are nested in restrooms, we have to fetch restrooms and extract them.
   * NOTE: This is not ideal for performance if there are many restrooms, 
   * but it matches the current database schema.
   * The client requested "It should show all restrooms/reviews that it finds created by the user logged in."
   * @param {string} userId - The user ID to filter by
   */
  async getUserReviews(userId) {
    if (!userId) return [];
    try {
      // We can't easily query nested array elements in Firestore across all documents 
      // without using array-contains, but userId is a property of an object in the reviews array.
      // Firestore doesn't support "where reviews array contains object with userId == X".
      // So we have to fetch restrooms. 
      // To optimize slightly, we could try to query restrooms where 'reviews' array is not empty, 
      // but Firestore doesn't support that either.
      
      // For now, we fetch all restrooms and filter in memory, 
      // or we query for ALL restrooms and then filter.
      // Given the current architecture, this is the most straightforward way.
      // A better way would be a top-level reviews collection.
      
      const restroomsCol = collection(this.db, 'restrooms');
      const snapshot = await getDocs(restroomsCol);
      
      const userReviews = [];
      snapshot.docs.forEach(doc => {
        const restroomData = doc.data();
        if (restroomData.reviews && Array.isArray(restroomData.reviews)) {
          const reviews = restroomData.reviews
            .filter(r => r.userId === userId)
            .map(r => ({
              ...r,
              restroomId: doc.id,
              restroomName: restroomData.name
            }));
          userReviews.push(...reviews);
        }
      });
      
      return userReviews;
    } catch (error) {
      logger.error('❌ Error getting user reviews:', error);
      return [];
    }
  }

  /**
   * Get all restrooms with visibility filtering
   * @param {boolean} isAdmin - Is requesting user an admin
   * @param {string} currentUserId - Current user's ID for creator visibility
   * @param {object} options - Optional filters (limit, geohashPrefixes)
   */
  async getRestrooms(isAdmin = false, currentUserId = null, options = {}) {
    try {
      const { limit: queryLimit, geohashPrefixes } = options;
      
      // Import verification config dynamically to avoid circular dependency
      const { getVerificationModeSync, VERIFICATION_MODES } = 
        await import('../config/verificationConfig.js');
      
      const verificationMode = getVerificationModeSync();
      
      const collectionName = (geohashPrefixes && geohashPrefixes.length > 0) ? 'restroom_map_index' : 'restrooms';
      const restroomsCol = collection(this.db, collectionName);
      let q;

      if (geohashPrefixes && geohashPrefixes.length > 0) {
        // Multi-query for multiple geohash prefixes
        const results = await Promise.all(geohashPrefixes.map(async (prefix) => {
          const { start, end } = (await import('../utils/geohashUtils.js')).getGeohashRangeForPrefix(prefix);
          const prefixQuery = query(
            restroomsCol,
            where('geohash', '>=', start),
            where('geohash', '<=', end),
            ...(queryLimit ? [limit(queryLimit)] : [])
          );
          const snapshot = await getDocs(prefixQuery);
          return snapshot.docs;
        }));
        
        // Flatten and deduplicate
        const allDocs = results.flat();
        const seen = new Set();
        const uniqueDocs = allDocs.filter(doc => {
          if (seen.has(doc.id)) return false;
          seen.add(doc.id);
          return true;
        });
        
        // If we have a limit, apply it after merging (approximate)
        const finalDocs = queryLimit ? uniqueDocs.slice(0, queryLimit) : uniqueDocs;
        
        return this._processRestroomDocs(finalDocs, isAdmin, currentUserId, verificationMode);
      } else {
        // Simple query (limited or full)
        q = queryLimit ? query(restroomsCol, limit(queryLimit)) : restroomsCol;
        const restroomSnapshot = await getDocs(q);
        return this._processRestroomDocs(restroomSnapshot.docs, isAdmin, currentUserId, verificationMode);
      }
    } catch (error) {
      logger.error('❌ Error getting restrooms:', error);
      throw new Error('Failed to load restrooms from Firebase');
    }
  }

  /**
   * Helper to process restroom documents consistently
   * @private
   */
  _processRestroomDocs(docs, isAdmin, currentUserId, verificationMode) {
    const { VERIFICATION_MODES } = require('../config/verificationConfig.js');
    
    return docs
      .map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || new Date().toISOString(),
          latitude: typeof data.latitude === 'number' ? data.latitude : parseFloat(data.latitude),
          longitude: typeof data.longitude === 'number' ? data.longitude : parseFloat(data.longitude)
        };
      })
      .filter(restroom => {
        const isValid = 
          restroom.latitude != null && 
          !isNaN(restroom.latitude) &&
          restroom.longitude != null && 
          !isNaN(restroom.longitude) &&
          restroom.name;
        
        if (!isValid) {
          logger.warn('⚠️ Invalid restroom filtered out:', restroom);
        }
        return isValid;
      })
      .filter(restroom => {
        if (verificationMode === VERIFICATION_MODES.BETA) return true;
        if (isAdmin) return true;
        if (currentUserId && restroom.createdBy === currentUserId) return true;
        return restroom.verified === true;
      });
  }

  /**
   * Save a new restroom
   */
  async saveRestroom(restroomData) {
    try {
      const restroomsCol = collection(this.db, 'restrooms');
      
      // Use the initial review that was created in DataContext (now has complete structure)
      const initialReview = restroomData.reviews && restroomData.reviews.length > 0 
        ? restroomData.reviews[0] 
        : (() => {
            console.error('❌ No review provided by DataContext - this should not happen!');
            // Emergency fallback - but this indicates a code issue
            return {
              id: `review-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              userId: restroomData.createdBy,
              username: restroomData.createdByUsername,
              rating: restroomData.rating,
              comment: restroomData.description || '',
              reviewerRestroomType: restroomData.reviewerRestroomType || 'unisex',
              createdAt: new Date().toISOString(),
              likes: 0,
              likedBy: []
            };
          })();
      
      // Prepare data for Firestore
      const firestoreData = {
        ...restroomData,
        createdAt: serverTimestamp(),
        reviews: [initialReview], // Use the review with proper ID
        reports: [],
        averageRating: restroomData.rating, // Use initial rating
        ratingCount: 1, // Count the initial add as 1 review
        totalRating: restroomData.rating // Initial total rating
      };
      
      // Remove the temporary ID if present
      delete firestoreData.id;
      
      logger.log('💾 Saving restroom to Firebase:', restroomData.name);
      const docRef = await addDoc(restroomsCol, firestoreData);
      logger.log('✅ Restroom saved with ID:', docRef.id);
      
      return true;
    } catch (error) {
      logger.error('❌ Error saving restroom:', error);
      throw new Error('Failed to save restroom to Firebase');
    }
  }

  /**
   * Update a restroom
   */
  async updateRestroom(restroomId, updates) {
    try {
      const restroomRef = doc(this.db, 'restrooms', restroomId);
      await updateDoc(restroomRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });
      logger.log('✅ Restroom updated:', restroomId);
      return true;
    } catch (error) {
      logger.error('❌ Error updating restroom:', error);
      throw error;
    }
  }

  /**
   * Verify a single restroom (admin only)
   */
  async verifyRestroom(restroomId, adminUserId) {
    try {
      const restroomRef = doc(this.db, 'restrooms', restroomId);
      await updateDoc(restroomRef, {
        verified: true,
        verifiedAt: new Date().toISOString(),
        verifiedBy: adminUserId,
        updatedAt: serverTimestamp()
      });
      logger.log('✅ Restroom verified:', restroomId);
      return true;
    } catch (error) {
      logger.error('❌ Error verifying restroom:', error);
      throw error;
    }
  }

  /**
   * Merge one restroom into another
   * @param {string} sourceId - ID of the restroom to be removed
   * @param {string} targetId - ID of the restroom to keep
   * @returns {Promise<boolean>}
   */
  async mergeRestrooms(sourceId, targetId) {
    try {
      const sourceRef = doc(this.db, 'restrooms', sourceId);
      const targetRef = doc(this.db, 'restrooms', targetId);

      await runTransaction(this.db, async (transaction) => {
        const sourceDoc = await transaction.get(sourceRef);
        const targetDoc = await transaction.get(targetRef);

        if (!sourceDoc.exists()) throw new Error('Source restroom not found');
        if (!targetDoc.exists()) throw new Error('Target restroom not found');

        const sourceData = sourceDoc.data();
        const targetData = targetDoc.data();

        const sourceReviews = sourceData.reviews || [];
        const targetReviews = targetData.reviews || [];
        const sourceReports = sourceData.reports || [];
        const targetReports = targetData.reports || [];

        // Combine reviews and reports
        const mergedReviews = [...targetReviews, ...sourceReviews];
        const mergedReports = [...targetReports, ...sourceReports];

        // Recalculate ratings
        const totalRating = mergedReviews.reduce((sum, r) => sum + (r.rating || 0), 0);
        const ratingCount = mergedReviews.length;
        const averageRating = ratingCount > 0 ? Math.round((totalRating / ratingCount) * 10) / 10 : 0;

        // Update target restroom
        transaction.update(targetRef, {
          reviews: mergedReviews,
          reports: mergedReports,
          totalRating: totalRating,
          ratingCount: ratingCount,
          averageRating: averageRating,
          updatedAt: serverTimestamp()
        });

        // Delete source restroom
        transaction.delete(sourceRef);
      });

      logger.log(`✅ Successfully merged restroom ${sourceId} into ${targetId}`);
      return true;
    } catch (error) {
      logger.error('❌ Error merging restrooms:', error);
      throw error;
    }
  }

  /**
   * Delete a restroom
   */
  async deleteRestroom(restroomId) {
    try {
      const restroomRef = doc(this.db, 'restrooms', restroomId);
      const mapIndexRef = doc(this.db, 'restroom_map_index', restroomId);
      const batch = writeBatch(this.db);
      batch.delete(restroomRef);
      batch.delete(mapIndexRef);
      await batch.commit();
      logger.log('✅ Restroom deleted:', restroomId);
      return true;
    } catch (error) {
      logger.error('❌ Error deleting restroom:', error);
      throw error;
    }
  }

  /**
   * Delete a user from Firestore
   * Note: This doesn't delete the Firebase Auth account, just the user profile
   */
  async deleteUser(userId) {
    try {
      const userRef = doc(this.db, 'users', userId);
      await deleteDoc(userRef);
      logger.log('✅ User deleted:', userId);
      return true;
    } catch (error) {
      logger.error('❌ Error deleting user:', error);
      throw error;
    }
  }

  // ==================== REVIEW OPERATIONS ====================

  /**
   * Add a review to a restroom
   * Also handles verification logic based on mode
   */
  async addReview(restroomId, reviewData, reviewerLocation = null) {
    try {
      // Get the restroom
      const restroomRef = doc(this.db, 'restrooms', restroomId);
      const restroomDoc = await getDoc(restroomRef);
      
      if (!restroomDoc.exists()) {
        throw new Error('Restroom not found');
      }
      
      const restroom = { id: restroomDoc.id, ...restroomDoc.data() };
      const reviews = restroom.reviews || [];
      
      // Add new review
      const newReview = {
        ...reviewData,
        id: `review-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        likes: 0,
        likedBy: []
      };
      reviews.push(newReview);
      
      // Calculate new average rating
      const totalRating = reviews.reduce((sum, r) => sum + r.rating, 0);
      const averageRating = totalRating / reviews.length;
      
      // Check if this review should trigger verification
      const shouldVerify = await this.checkVerificationTrigger(restroom, newReview, reviewerLocation);
      
      // Prepare update data
      const updateData = {
        reviews,
        averageRating: Math.round(averageRating * 10) / 10,
        ratingCount: reviews.length,
        totalRating
      };
      
      // Update restroom features if provided in review
      if (reviewData.hasOwnProperty('isClean')) updateData.isClean = reviewData.isClean;
      if (reviewData.hasOwnProperty('hasHandSoap')) updateData.hasHandSoap = reviewData.hasHandSoap;
      if (reviewData.hasOwnProperty('hasPaperTowels')) updateData.hasPaperTowels = reviewData.hasPaperTowels;
      if (reviewData.hasOwnProperty('hasChangingTable')) updateData.hasChangingTable = reviewData.hasChangingTable;
      if (reviewData.hasOwnProperty('hasHandicapAccess')) updateData.hasHandicapAccess = reviewData.hasHandicapAccess;
      if (reviewData.hasOwnProperty('hasBidet')) updateData.hasBidet = reviewData.hasBidet;
      if (reviewData.hasOwnProperty('requiresKey')) updateData.requiresKey = reviewData.requiresKey;
      if (reviewData.hasOwnProperty('hasShowers')) updateData.hasShowers = reviewData.hasShowers;
      if (reviewData.hasOwnProperty('restroomType')) updateData.restroomType = reviewData.restroomType;
      if (reviewData.hasOwnProperty('stallGapSize')) updateData.stallGapSize = reviewData.stallGapSize;
      
      // Add verification if triggered
      if (shouldVerify && !restroom.verified) {
        updateData.verified = true;
        updateData.verifiedAt = new Date().toISOString();
        updateData.verifiedBy = reviewData.userId;
        logger.log('✅ Restroom verified!', restroomId);
      }
      
      // Update restroom
      await updateDoc(restroomRef, updateData);
      
      logger.log('✅ Review added to restroom:', restroomId);
      return true;
    } catch (error) {
      logger.error('❌ Error adding review:', error);
      throw error;
    }
  }
  
  /**
   * Check if review should trigger verification
   */
  async checkVerificationTrigger(restroom, newReview, reviewerLocation) {
    // Dynamic import to avoid circular dependency
    const { getVerificationModeSync, getVerificationRadius, VERIFICATION_MODES, VERIFICATION_CONFIG } = 
      await import('../config/verificationConfig.js');
    
    const mode = getVerificationModeSync();
    const minReviews = VERIFICATION_CONFIG.minReviewsRequired[mode];
    
    // Already verified
    if (restroom.verified) {
      return false;
    }
    
    // Check mode-specific logic
    if (mode === VERIFICATION_MODES.BETA) {
      // Beta: Just need minimum reviews (creator's counts)
      return restroom.reviews.length >= minReviews;
    }
    
    // Production: Need independent review within radius
    if (mode === VERIFICATION_MODES.PRODUCTION) {
      // Must be different user
      if (newReview.userId === restroom.createdBy) {
        return false;
      }
      
      // Check distance if location provided
      if (reviewerLocation) {
        const distance = this.calculateDistance(
          restroom.latitude,
          restroom.longitude,
          reviewerLocation.lat,
          reviewerLocation.lng
        );
        
        const maxDistance = getVerificationRadius(); // 0.015 km = 15 meters
        
        if (distance <= maxDistance) {
          // Independent review within 15 meters - verify!
          return true;
        } else {
          logger.log(`⚠️ Review too far for verification: ${(distance * 1000).toFixed(1)}m (max: 15m)`);
          return false;
        }
      }
      
      // No location provided - can't verify distance
      logger.warn('⚠️ Review location not provided - cannot verify distance');
      return false;
    }
    
    return false;
  }
  
  /**
   * Calculate distance between two coordinates (Haversine formula)
   * @returns {number} Distance in kilometers
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371; // Radius of Earth in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  // ==================== REPORT OPERATIONS ====================

  /**
   * Report a restroom issue
   */
  async reportRestroom(restroomId, reportData) {
    try {
      const restroomRef = doc(this.db, 'restrooms', restroomId);
      const restroomDoc = await getDoc(restroomRef);
      
      if (!restroomDoc.exists()) {
        throw new Error('Restroom not found');
      }
      
      const restroom = restroomDoc.data();
      const reports = restroom.reports || [];
      
      // Add new report with support for reasonType
      const newReport = {
        ...reportData,
        reasonType: reportData.reasonType || 'other', // Default to 'other' for backward compatibility
        createdAt: new Date().toISOString()
      };
      reports.push(newReport);
      
      // Update restroom
      await updateDoc(restroomRef, {
        reports
      });
      
      logger.log('✅ Report added to restroom:', restroomId);
      return true;
    } catch (error) {
      logger.error('❌ Error reporting restroom:', error);
      throw error;
    }
  }

  // ==================== CONFIGURATION OPERATIONS ====================

  /**
   * Get verification mode from Firestore config
   * Returns 'beta' or 'production'
   */
  async getVerificationMode() {
    try {
      const configRef = doc(this.db, 'config', 'verification');
      const configDoc = await getDoc(configRef);
      
      if (configDoc.exists()) {
        const data = configDoc.data();
        return data.mode || 'production'; // Default to production
      }
      
      // Config doesn't exist yet, return default
      logger.log('ℹ️ Verification config not found, using default: production');
      return 'production';
    } catch (error) {
      logger.error('❌ Error getting verification mode:', error);
      return 'production'; // Fallback to production on error
    }
  }

  /**
   * Set verification mode in Firestore (admin only)
   * @param {string} mode - 'beta' or 'production'
   */
  async setVerificationMode(mode) {
    try {
      const configRef = doc(this.db, 'config', 'verification');
      
      await setDoc(configRef, {
        mode: mode,
        updatedAt: serverTimestamp(),
        description: mode === 'production' 
          ? 'Community verification - requires 2 users within 15m'
          : `Custom mode: ${mode}`
      }, { merge: true });
      
      logger.log(`✅ Verification mode set to: ${mode}`);
      return true;
    } catch (error) {
      logger.error('❌ Error setting verification mode:', error);
      throw error;
    }
  }

  /**
   * Update restroom reviews (for migration)
   * @param {string} restroomId - Restroom ID
   * @param {Array} reviews - Updated reviews array
   * @returns {Promise<boolean>} Success status
   */
  async updateRestroomReviews(restroomId, reviews) {
    try {
      const restroomRef = doc(this.db, 'restrooms', restroomId);
      await updateDoc(restroomRef, { reviews });
      logger.log(`✅ Updated reviews for restroom: ${restroomId}`);
      return true;
    } catch (error) {
      logger.error('Error updating restroom reviews:', error);
      throw error;
    }
  }

  // ==================== REVIEW LIKES ====================

  /**
   * Like or unlike a review
   * @param {string} reviewId - Review ID
   * @param {string} userId - User ID who is liking/unliking
   * @returns {Promise<Object>} Updated review data
   */
  async likeReview(reviewId, userId) {
    try {
      logger.log('🔍 Looking for review:', reviewId, 'by user:', userId);
      
      // Find the restroom that contains this review
      const restroomsRef = collection(this.db, 'restrooms');
      const querySnapshot = await getDocs(restroomsRef);
      
      let targetRestroomId = null;
      let targetReviewIndex = -1;
      let foundReview = null;
      
      // Search through all restrooms to find the one containing this review
      for (const doc of querySnapshot.docs) {
        const restroom = doc.data();
        const reviews = restroom.reviews || [];
        
        // Try to find review by exact ID match first
        let reviewIndex = reviews.findIndex(r => r.id === reviewId);
        
        // If not found by exact ID, try fallback matching for older reviews
        if (reviewIndex === -1) {
          reviewIndex = reviews.findIndex(r => {
            // Generate fallback ID to compare
            const fallbackId = `${r.userId}-${r.createdAt}-${r.rating}`;
            return fallbackId === reviewId;
          });
        }
        
        if (reviewIndex !== -1) {
          targetRestroomId = doc.id;
          targetReviewIndex = reviewIndex;
          foundReview = reviews[reviewIndex];
          logger.log('✅ Found review in restroom:', doc.id, 'at index:', reviewIndex);
          break; // Stop searching once found
        }
      }
      
      if (targetRestroomId === null || targetReviewIndex === -1) {
        logger.error('❌ Review not found. Searched through', querySnapshot.docs.length, 'restrooms');
        throw new Error(`Review not found in any restroom (searched ${querySnapshot.docs.length} restrooms)`);
      }
      
      // Update the review in the restroom document
      const restroomRef = doc(this.db, 'restrooms', targetRestroomId);
      
      const result = await runTransaction(this.db, async (transaction) => {
        const restroomDoc = await transaction.get(restroomRef);
        
        if (!restroomDoc.exists()) {
          throw new Error('Restroom not found');
        }
        
        const restroomData = restroomDoc.data();
        const reviews = restroomData.reviews || [];
        const review = reviews[targetReviewIndex];
        
        if (!review) {
          throw new Error('Review not found in restroom during transaction');
        }
        
        // Initialize likedBy array if it doesn't exist
        if (!review.likedBy) {
          review.likedBy = [];
        }
        
        // Toggle like
        let isLiked = false;
        
        if (review.likedBy.includes(userId)) {
          // Unlike
          review.likedBy = review.likedBy.filter(id => id !== userId);
          review.likes = Math.max(0, (review.likes || 0) - 1);
          isLiked = false;
        } else {
          // Like
          review.likedBy.push(userId);
          review.likes = (review.likes || 0) + 1;
          isLiked = true;
        }
        
        // Update the reviews array
        reviews[targetReviewIndex] = review;
        
        // Update the restroom document
        transaction.update(restroomRef, { reviews });
        
        return {
          reviewId: review.id,
          likes: review.likes,
          likedBy: review.likedBy,
          isLiked
        };
      });
      
      logger.log('✅ Review like updated successfully:', result);
      return result;
    } catch (error) {
      logger.error('❌ Error liking review:', error);
      throw error;
    }
  }

  /**
   * Get user's total likes across all their reviews
   * @param {string} userId - User ID
   * @returns {Promise<number>} Total likes received
   */
  async getUserTotalLikes(userId) {
    try {
      const reviewsQuery = query(
        collection(this.db, 'reviews'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(reviewsQuery);
      let totalLikes = 0;
      
      querySnapshot.forEach((doc) => {
        const reviewData = doc.data();
        totalLikes += reviewData.likes || 0;
      });
      
      return totalLikes;
    } catch (error) {
      logger.error('Error getting user total likes:', error);
      return 0;
    }
  }

  /**
   * Get all reviews for export, including restroom name and reviewer username.
   * This function fetches all restrooms and then extracts all embedded reviews,
   * enriching them with parent restroom data.
   * @returns {Array<Object>} A flattened array of review objects with restroom and user details.
   */
  async getAllReviewsForExport() {
    try {
      logger.log('🌐 Fetching all reviews for export...');
      const restroomsCol = collection(this.db, 'restrooms');
      const restroomSnapshot = await getDocs(restroomsCol);
      
      const allReviewsData = [];
      
      restroomSnapshot.forEach(restroomDoc => {
        const restroom = restroomDoc.data();
        const restroomId = restroomDoc.id;
        const restroomName = restroom.name;
        
        if (restroom.reviews && Array.isArray(restroom.reviews)) {
          restroom.reviews.forEach(review => {
            allReviewsData.push({
              reviewId: review.id,
              restroomId: restroomId,
              restroomName: restroomName,
              reviewerUsername: review.username,
              reviewerUserId: review.userId,
              rating: review.rating,
              comment: review.comment,
              isClean: review.isClean,
              hasHandSoap: review.hasHandSoap,
              hasPaperTowels: review.hasPaperTowels,
              hasChangingTable: review.hasChangingTable,
              hasHandicapAccess: review.hasHandicapAccess,
              hasBidet: review.hasBidet,
              requiresKey: review.requiresKey,
              hasShowers: review.hasShowers,
              restroomType: review.restroomType,
              stallGapSize: review.stallGapSize,
              likes: review.likes,
              createdAt: review.createdAt,
            });
          });
        }
      });
      
      logger.log(`✅ Prepared ${allReviewsData.length} reviews for export.`);
      return allReviewsData;
    } catch (error) {
      logger.error('❌ Error getting all reviews for export:', error);
      throw new Error('Failed to get all reviews for export');
    }
  }

  /**
   * Search restrooms using the SearchRestrooms Cloud Function
   * @param {string} searchQuery - Search query
   * @returns {Promise<Array>} Search results
   */
  async searchRestrooms(searchQuery) {
    try {
      const searchRestroomsFn = httpsCallable(functions, 'SearchRestrooms');
      const result = await searchRestroomsFn({ query: searchQuery });
      
      if (result.data && result.data.success) {
        return result.data.results.map(data => {
          // Handle Firestore Timestamp objects (_seconds, _nanoseconds) that might come from Cloud Functions
          const convertTimestamp = (ts) => {
            if (!ts) return new Date().toISOString();
            if (typeof ts.toDate === 'function') return ts.toDate().toISOString();
            if (ts._seconds !== undefined) return new Date(ts._seconds * 1000).toISOString();
            if (typeof ts === 'string') return ts;
            return new Date().toISOString();
          };

          return {
            ...data,
            createdAt: convertTimestamp(data.createdAt),
            updatedAt: convertTimestamp(data.updatedAt),
            verifiedAt: convertTimestamp(data.verifiedAt),
            latitude: typeof data.latitude === 'number' ? data.latitude : parseFloat(data.latitude),
            longitude: typeof data.longitude === 'number' ? data.longitude : parseFloat(data.longitude)
          };
        });
      }
      return [];
    } catch (error) {
      logger.error('❌ Error calling SearchRestrooms function:', error);
      return [];
    }
  }

  /**
   * Get nearby places for a location using AreYouAt Cloud Function
   * @param {number} latitude 
   * @param {number} longitude 
   * @returns {Promise<Array>} List of places
   */
  async areYouAt(latitude, longitude) {
    try {
      const areYouAtFn = httpsCallable(functions, 'AreYouAt');
      const result = await areYouAtFn({ latitude, longitude });
      
      if (result.data && result.data.success) {
        return result.data.places || [];
      }
      return [];
    } catch (error) {
      logger.error('❌ Error calling AreYouAt function:', error);
      throw error;
    }
  }

  // ==================== HEALTH CHECK ====================

  /**
   * Get top liked reviews with time filter
   */
  async getTopLikedReviews(timeFilter = 'week', limitCount = 10) {
    try {
      const restroomsCol = collection(this.db, 'restrooms');
      const now = new Date();
      let startDate;
      
      // Calculate start date based on time filter
      switch(timeFilter) {
        case 'day':
          startDate = new Date(now.setHours(0, 0, 0, 0)); // Today at midnight
          break;
        case 'week':
          startDate = new Date(now.setDate(now.getDate() - 7)); // 7 days ago
          break;
        case 'month':
          startDate = new Date(now.setMonth(now.getMonth() - 1)); // 30 days ago
          break;
        default:
          startDate = new Date(now.setDate(now.getDate() - 7)); // Default to week
      }
      
      logger.log('🔍 Royal Reviews Query - Time filter:', timeFilter, 'Start date:', startDate.toISOString());
      
      // Get restrooms collection (reviews are embedded in restroom documents)
      const q = query(
        restroomsCol,
        limit(100) // Get 100 restrooms to extract reviews from
      );
      
      const querySnapshot = await getDocs(q);
      const allReviews = [];
      
      logger.log('📊 Royal Reviews Query - Found restrooms:', querySnapshot.docs.length);
      
      // Extract reviews from each restroom
      for (const doc of querySnapshot.docs) {
        const restroomData = doc.data();
        const reviews = restroomData.reviews || [];
        
        logger.log(`📝 Processing restroom ${restroomData.name} with ${reviews.length} reviews`);
        
        for (const review of reviews) {
          // Add full restroom info to each review for navigation
          const reviewWithRestroom = {
            ...review,
            restroom: {
              id: doc.id,
              name: restroomData.name,
              address: restroomData.address,
              category: restroomData.category,
              latitude: restroomData.latitude,
              longitude: restroomData.longitude,
              averageRating: restroomData.averageRating,
              rating: restroomData.rating,
              reviewCount: restroomData.reviewCount
            }
          };
          
          logger.log('📝 Processing review:', {
            id: review.id || 'unknown',
            likes: review.likes,
            createdAt: review.createdAt,
            restroomId: doc.id,
            restroomName: restroomData.name,
            hasLikes: !!(review.likes && review.likes > 0),
            hasCoordinates: !!(restroomData.latitude && restroomData.longitude),
            coordinates: `${restroomData.latitude}, ${restroomData.longitude}`
          });
          
          allReviews.push(reviewWithRestroom);
        }
      }
      
      logger.log('📊 Total reviews extracted:', allReviews.length);
      
      // Client-side filtering by time period
      let filteredReviews = allReviews;
      if (timeFilter !== 'all') {
        filteredReviews = allReviews.filter(review => {
          const reviewDate = new Date(review.createdAt?.toDate?.() || review.createdAt || 0);
          return reviewDate >= startDate;
        });
      }
      
      // Client-side sorting: first by likes (descending), then by createdAt (descending)
      const sortedReviews = filteredReviews.sort((a, b) => {
        // First sort by likes (descending)
        const aLikes = a.likes || 0;
        const bLikes = b.likes || 0;
        
        if (bLikes !== aLikes) {
          return bLikes - aLikes;
        }
        
        // Then sort by date (descending)
        const aDate = new Date(a.createdAt?.toDate?.() || a.createdAt || 0);
        const bDate = new Date(b.createdAt?.toDate?.() || b.createdAt || 0);
        return bDate - aDate;
      });
      
      // Filter to reviews with at least 1 like and limit to requested count
      const finalReviews = sortedReviews
        .filter(review => (review.likes || 0) > 0)
        .slice(0, limitCount);
      
      logger.log(`✅ Loaded ${finalReviews.length} top liked reviews (${timeFilter}) from ${allReviews.length} total reviews (${filteredReviews.length} after time filter)`);
      return finalReviews;
    } catch (error) {
      logger.error('❌ Error getting top liked reviews:', error);
      return [];
    }
  }

  /**
   * Test Firebase connection
   */
  async testConnection() {
    try {
      // Try to read from Firestore
      const usersCol = collection(this.db, 'users');
      await getDocs(usersCol);
      logger.log('✅ Firebase connection successful');
      return true;
    } catch (error) {
      logger.error('❌ Firebase connection failed:', error);
      return false;
    }
  }

  // ==================== QR CODE MANAGEMENT ====================

  /**
   * Save QR code to Firestore
   * @param {object} qrData - QR code data
   */
  async saveQRCode(qrData) {
    try {
      const qrCodesRef = collection(this.db, 'qrCodes');
      const docRef = await addDoc(qrCodesRef, {
        ...qrData,
        createdAt: new Date().toISOString(),
        lastGenerated: new Date().toISOString(),
        generationCount: 1,
        isActive: true
      });
      
      logger.log('✅ QR Code saved:', docRef.id);
      return { id: docRef.id, ...qrData };
    } catch (error) {
      logger.error('❌ Error saving QR code:', error);
      throw error;
    }
  }

  /**
   * Get all QR codes from Firestore
   */
  async getQRCodes() {
    try {
      const qrCodesRef = collection(this.db, 'qrCodes');
      const q = query(qrCodesRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const qrCodes = [];
      
      querySnapshot.forEach((doc) => {
        qrCodes.push({ id: doc.id, ...doc.data() });
      });
      
      logger.log(`✅ Loaded ${qrCodes.length} QR codes from Firebase`);
      return qrCodes;
    } catch (error) {
      logger.error('❌ Error getting QR codes:', error);
      throw error;
    }
  }

  /**
   * Update QR code in Firestore
   * @param {string} id - QR code document ID
   * @param {object} updates - Updates to apply
   */
  async updateQRCode(id, updates) {
    try {
      const qrCodeRef = doc(this.db, 'qrCodes', id);
      await updateDoc(qrCodeRef, {
        ...updates,
        lastGenerated: new Date().toISOString()
      });
      
      logger.log('✅ QR Code updated:', id);
      return true;
    } catch (error) {
      logger.error('❌ Error updating QR code:', error);
      throw error;
    }
  }

  /**
   * Delete QR code from Firestore
   * @param {string} id - QR code document ID
   */
  async deleteQRCode(id) {
    try {
      const qrCodeRef = doc(this.db, 'qrCodes', id);
      await deleteDoc(qrCodeRef);
      
      logger.log('✅ QR Code deleted:', id);
      return true;
    } catch (error) {
      logger.error('❌ Error deleting QR code:', error);
      throw error;
    }
  }

  /**
   * Get QR code by referral code
   * @param {string} referralCode - Referral code to search for
   */
  async getQRCodeByReferralCode(referralCode) {
    try {
      const qrCodesRef = collection(this.db, 'qrCodes');
      const q = query(qrCodesRef, where('referralCode', '==', referralCode));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      
      return null;
    } catch (error) {
      logger.error('❌ Error getting QR code by referral code:', error);
      throw error;
    }
  }
}

// Export singleton instance
const firebaseService = new FirebaseService();
export default firebaseService;

// Export the existing instances for use in other context providers
export { db, auth, functions };
